import { Layer } from "@/Utils/hierarchy";

import {
  AllRevenueConfig,
  AllRevenueDynamic,
  ColorConfig,
  ConfigData,
  DirectRevenueConfigDynamic,
  DirectRevenueConfigStatic,
  MarketingLayer,
  SaleTypes,
  SharedRevenueConfigDynamic,
  SharedRevenueConfigStatic,
  SpendInsightsConfig,
} from "./types";

export type SalesColor = {
  borderColor: string;
  color: string;
  mappedName: string;
};

export const defaultMarketingLayer: MarketingLayer = {
  upperLayer: "Brand",
  middleLayer: "Category",
  lowerLayer: "Product",
};

export const defaultDirectRevenueConfig: DirectRevenueConfigDynamic & DirectRevenueConfigStatic = {
  cot: false,
  cotHeader: "",
  cotLineOneHeader: "",
  cotLineTwoHeader: "",
  cotLineOneDescription: "",
  cotLineTwoDescription: "",
  yAxisLabelOptionOne: "",
  yAxisLabelOptionTwo: "",
  totalBrandValuesLabel1: "",
  totalBrandValuesLabel2: "",
  totalBrandValuesLabel3: "",
  totalProductValuesLabel1: "",
  totalProductValuesLabel2: "",
  totalProductValuesLabel3: "",
  totalValuesLabel1: "",
  totalValuesLabel2: "",
  totalValuesLabel3: "",
  brand: {
    revenue: "",
    spend: "",
    roi: "",
    aggregate: {
      revenue: "",
      spend: "",
      roi: "",
    },
  },
  category: {
    revenue: "",
    spend: "",
    roi: "",
    aggregate: {
      revenue: "",
      spend: "",
      roi: "",
    },
  },
  product: {
    revenue: "",
    spend: "",
    roi: "",
    aggregate: {
      revenue: "",
      spend: "",
      roi: "",
    },
  },
};

export const defaultSpendInsightsConfig: SpendInsightsConfig = {
  tabNames: {
    allRevenue: "",
    brand: {
      optimizer: "",
      soe: "",
      roi: "",
      breakdownExplorer: "",
      breakdownExplorerBrand: "",
    },
    category: {
      optimizer: "",
      soe: "",
      roi: "",
      breakdownExplorer: "",
      breakdownExplorerBrand: "",
    },
    product: {
      optimizer: "",
      soe: "",
      roi: "",
      breakdownExplorer: "",
      breakdownExplorerBrand: "",
    },
  },
  header: "",
  description: "",
  tooltip: "",
  detailButtonText: "",
};

export const defaultAllRevenueConfig: AllRevenueConfig & AllRevenueDynamic = {
  legendShared: "",
  subTotalDirectPercentDesc: "",
  subTotalDirectDollarDesc: "",
  subTotalSharedPercentDesc: "",
  legendHalo: "",
  subTotalHaloDollarDesc: "",
  yAxisLabel: "",
  legendDirect: "",
  totalSpendDesc: "",
  totalReturnDesc: "",
  subTotalSharedDollarDesc: "",
  subTotalHaloPercentDesc: "",

  brand: {
    dollarROI: "",
    percentROI: "",
    legend: "",
    totalRevenue: "",
    totalSpend: "",
  },
  category: {
    dollarROI: "",
    percentROI: "",
    legend: "",
    totalRevenue: "",
    totalSpend: "",
  },
  product: {
    dollarROI: "",
    percentROI: "",
    legend: "",
    totalRevenue: "",
    totalSpend: "",
  },
};

export const defaultSharedRevenueConfig: SharedRevenueConfigDynamic & SharedRevenueConfigStatic = {
  totalTitle: "",
  yAxisLabel: "",
  totalReturnValDesc: "",
  totalSOEDesc: "",
  tableHeader: "",
  brand: {
    revenue: "",
    totalSOE: "",
  },
  category: {
    revenue: "",
    totalSOE: "",
  },
  product: {
    revenue: "",
    totalSOE: "",
  },
};

export const defaultConfigData: ConfigData = {
  mapping: {
    other: {},
    meta: {},
    geography: {},
  },
  graphs: {
    SalesByComposition: {},
    saturationCurves: {},
    environmentalConditions: {},
    bubbleChart: {},
    allRevenue: defaultAllRevenueConfig,
    spendInsight: defaultSpendInsightsConfig,
    directRevenue: defaultDirectRevenueConfig,
    sharedRevenue: defaultSharedRevenueConfig,
  },
  dashboard: {
    cfo: {
      description: "",
      title: "",
      cardTitle: "",
    },
    cmo: {
      description: "",
      title: "",
      cardTitle: "",
    },
    hidePortfolio: false,
  },
  colors: {} as Record<SaleTypes, ColorConfig>,
  config: {
    showReturnInsteadOfRevenue: false,
    revenueLabel: "Revenue",
    currencySettings: {
      locale: "en-AU",
      currencyCode: "AUD",
    },
    currencySymbol: "$",
    marketingLayer: defaultMarketingLayer,
  },
  cfo: {
    PortfolioSales: {},
    RelianceBreakDown: {
      barChartHeader: "",
      description: "",
      title: "",
      yAxisHeadingRight1: "",
      yAxisHeadingRight2: "",
      yAxisHeadingRight3: "",
      yAxisHeadingRight5: "",
    },
    RelianceRanking: {},
    brandsAcrossPortfolio: {},
    header: {},
    marketingContribution: {},
    portfolioSalesByComposition: {},
    portfolioSummary: {},
    salesRelianceBreakdown: {},
    sectionTwoHeader: {},
  },
};

export const BRAND_PLACEHOLDER = "${brand}";
export const CATEGORY_PLACEHOLDER = "${category}";
export const PRODUCT_PLACEHOLDER = "${product}";

// Used when layer needs to be determined dynamically at runtime
export const LAYER_PLACEHOLDER = "${layer}";

export const FIXED_LAYER_PLACEHOLDERS: [string, Layer][] = [
  [BRAND_PLACEHOLDER, Layer.Brand],
  [CATEGORY_PLACEHOLDER, Layer.Category],
  [PRODUCT_PLACEHOLDER, Layer.Product],
];

export const SALES_COLORS: Record<string, SalesColor> = {
  base_sale: {
    borderColor: "#000000",
    color: "#1B1B1B",
    mappedName: "Base Sales",
  },
  brand_marketing_sale: {
    borderColor: "#BB0559",
    color: "#F971B0",
    mappedName: "${brand} Marketing Sales",
  },
  brand_sale: {
    borderColor: "#084785",
    color: "#1E7BD5",
    mappedName: "${brand} Sales",
  },
  category_marketing_sale: {
    borderColor: "#50C2FE",
    color: "#94D5F7",
    mappedName: "${category} Marketing Sales",
  },
  control_sale: {
    borderColor: "#000000",
    color: "#adadad",
    mappedName: "External Factors",
  },
  error_sale: {
    borderColor: "#0d0c0c",
    color: "#d9d9d9",
    mappedName: "Unattributed Sales",
  },
  external_sale: {
    borderColor: "#525252",
    color: "#969696",
    mappedName: "External Sales",
  },
  media_sale: {
    borderColor: "#0d0c0c",
    color: "#f0a04b",
    mappedName: "${product} Marketing Sales",
  },
  other_sale: {
    borderColor: "#0d0c0c",
    color: "#FFFFFF",
    mappedName: "Other Sales",
  },
  owned_sale: {
    borderColor: "#0d0c0c",
    color: "#E6D200",
    mappedName: "Owned Sales",
  },
  price_sale: {
    borderColor: "#000000",
    color: "#717171",
    mappedName: "Price Sales",
  },
  shared_marketing_sale: {
    borderColor: "#BB0559",
    color: "#F971B0",
    mappedName: "${brand} Marketing Sales",
  },
  total_sale: {
    borderColor: "#0d0c0c",
    color: "#0d0c0c",
    mappedName: "Total Sales",
  },
};

export default {
  BRAND_PLACEHOLDER,
  CATEGORY_PLACEHOLDER,
  PRODUCT_PLACEHOLDER,
  LAYER_PLACEHOLDER,
  FIXED_LAYER_PLACEHOLDERS,
};
